import React, { useState } from 'react';
import {
  Form, Control, actions as formActions, track
} from 'react-redux-form';
import { sum } from 'utils/arrays';
import FontAwesome from 'react-fontawesome';
import PropTypes from 'prop-types';
import {Button, OverlayTrigger, Tooltip} from 'react-bootstrap';
import {
  ensureKeyed, getMetaContent, parseCurrency, stringParams
} from 'utils';

import { formatCurrency, formatDate } from 'utils/i18n';
import classNames from 'classnames';
import BigNumber from 'bignumber.js';
import ControlGroup from '../forms/ControlGroup';
import DateSelector from '../forms/DateSelector';
import ModalComponent from '../ModalComponent';
import Placeholder from '../Placeholder';
import ValidationErrors from '../forms/ValidationErrors';
import {
  billingCategoryGroups as billingCategories,
  billingContacts,
  billingItemForm,
  billingItems,
  modals,
  schoolYears,
  billingConfigs as billingConfigsDuck
} from '../../ducks/index';
import duckify, { filterDuck, currentCustomer, singleDuck } from '../../utils/duckify';
import { titleize } from '../../utils/strings';
import { BillingItemType } from '../../propTypes';
import './styles/BillingItemList.scss';
import { MONTHS } from '../../constants';
import { ledgerLockCalendarParams, LedgerLockDateLabel } from '../shared/LedgerLock';

const formatBillingContact = c => `${c.contact.first_name} ${c.contact.last_name} (${c.school_year.name})`;

const categoryFromId = (id, categories) => categories.find((g) => (
  g.id === id || g.children.find((c) => c.id === id)
));

class BillingItemEditor extends React.Component {
  static propTypes = {
    type: PropTypes.string.isRequired,
    billingAccountId: PropTypes.string.isRequired,
    payerContactId: PropTypes.string.isRequired,
    repost: PropTypes.bool,
    reload: PropTypes.func,
    location: PropTypes.shape({
      search: PropTypes.string
    })
  };

  static defaultProps = {
    repost: false,
    reload: undefined,
    location: {}
  }

  static mapStateToProps = (state, ownProps) => {
    const billingContactsKey = stringParams({ billing_account_id: ownProps.billingAccountId });

    return {
      // Maps in "form", "model"
      ...state[billingItemForm.name],
      billingCategories: filterDuck(billingCategories, {}).all(state),
      billingContacts: billingContacts.all(state, billingContactsKey),
      billingContactsKey,
      schoolYears: schoolYears.all(state)
    };
  };

  constructor() {
    super();
    this.state = { billingItems: [], preview: false, preDistributedState: { amounts: [], distribution: '', type: '' } };
  }

  addAmount() {
    const { dispatch } = this.props;
    dispatch(formActions.push('billingItemForm.model.billing_item_amounts', { id: `amount-${Math.random()}` }));
  }

  removeAmount(index) {
    const { dispatch } = this.props;
    dispatch(formActions.remove('billingItemForm.model.billing_item_amounts', index));
  }

  validSchoolYears() {
    if (this.props.schoolYears.ready === false) {
      return [];
    }

    return this.props.schoolYears
      .filter(schoolYear => schoolYear.billing_enabled)
      .sort((a, b) => b.name.localeCompare(a.name)) // This should be removed if/when 'school_year' default scope is in descending order.
  }

  preview() {
    const { model, dispatch } = this.props;
    let token = getMetaContent('csrf-token');

    fetch('/api/billing_items/preview', {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
      method: 'PUT',
      body: JSON.stringify({ billing_item: billingItemForm.actions.transformToServer(model) })
    }).then((response) => (response.json()).then((data) => {
      // If this is just a single billing item, it's an errored object we returned to show errors with
      if (response.status !== 200) {
        dispatch(formActions.merge('billingItemForm.model', { errors: data.billing_item.errors }));
      } else {
        let undistributedAmounts = [];
        const { distribution, type } = model;
        if (distribution === 'auto' || distribution === 'zero') {
          undistributedAmounts = model.billing_item_amounts;
          dispatch(formActions.merge('billingItemForm.model', {
            distribution: '',
            billing_item_amounts: billingItemForm.actions.transformFromServer(data.updated_splits).billing_item_amounts,
            type: data.updated_splits.type
          }));
        }
        dispatch(formActions.change('billingItemForm.model.errors', {}));

        this.setState({
          billingItems: data.billing_items.map(item => billingItemForm.actions.transformFromServer(item)),
          preview: true,
          preDistributedState: {
            distribution,
            type,
            amounts: undistributedAmounts
          }
        });
      }
    }));
  }

  back() {
    this.setState({ preview: false });
  }

  undoAutoDistribute() {
    const { dispatch } = this.props;
    this.setState({ preview: false });
    dispatch(formActions.merge('billingItemForm.model', {
      distribution: this.state.preDistributedState.distribution,
      type: this.state.preDistributedState.type,
      billing_item_amounts: this.state.preDistributedState.amounts
    }));
  }

  save() {
    const { dispatch, model, reload } = this.props;

    dispatch(modals.actions.update({ saving: true }));
    dispatch(billingItemForm.actions.save({ ...model }))
      .then(() => dispatch(modals.actions.hide()))
      .then(() => dispatch(billingItems.actions.markDirty()))
      .then(() => { if (reload) { reload(false); } })
      .catch((response) => {
        if (!response.billing_item) {
          dispatch(modals.actions.hide());
          dispatch(modals.actions.show('UnexpectedError', {message: response.error}));
          // Reload the items just in case the edit succeeded, to give the user the opportunity to see that it
          // worked and not attempt to re-apply the changes
          dispatch(billingItems.actions.markDirty());
        }
      });
  }

  componentDidMount() {
    const { billingContactsKey, dispatch, payerContactId } = this.props;

    if (!this.props.billingCategories.length) {
      dispatch(filterDuck(billingCategories, {}).actions.load());
    }

    if (!this.props.billingContacts.length) {
      dispatch(billingContacts.actions.load({payer_contact_id: payerContactId}, billingContactsKey));
    }

    if (!this.props.schoolYears.length) {
      dispatch(schoolYears.actions.load());
    }

    dispatch(formActions.merge('billingItemForm.model', { post_type: 'once' }));
  }

  render() {
    const {
      billingCategories, billingContacts, model, saving, edit, repost, currentCustomer, billingConfig
    } = this.props;

    const amountsLocked = model.is_contract_charge;
    const linkedToEntity = !!(model.order_id || model.checklist_answer_id);
    const autoDistribute = model.distribution === 'auto';
    const zeroOut = model.distribution === 'zero';

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // Javascript zero-indexes months for special reasons
    const years = [currentYear - 1, currentYear, currentYear + 1];
    const saveText = amountsLocked ? 'Save' : (!repost && this.state.preview ? 'Confirm' : (repost && this.state.preview ? 'Repost' : ''));
    const saveClass = amountsLocked || (!repost && this.state.preview) || (repost && this.state.preview) ? '' : 'd-none disabled';
    const buttons = [
      <Button
        variant='primary'
        disabled={!this.state.preview}
        className={this.state.preview ? '' : 'd-none disabled'}
        onClick={this.state.preDistributedState.amounts.length > 0 ? () => this.undoAutoDistribute() : () => this.back()}
      >
        Previous
      </Button>,
      <Button
        variant='primary'
        disabled={!this.state.preview || this.state.preDistributedState.amounts.length === 0}
        className={this.state.preview && this.state.preDistributedState.amounts.length > 0 ? '' : 'd-none disabled'}
        onClick={() => this.back()}
      >
        Edit Splits
      </Button>,
      <Button
        variant='primary'
        disabled={saving}
        className={!this.state.preview && !amountsLocked ? '' : 'd-none disabled'}
        onClick={() => this.preview()}
      >
        Preview
      </Button>,
      <Button
        variant='primary'
        disabled={saving}
        className={saveClass}
        onClick={() => this.save(false)}
      >
        {saving ? 'Saving...' : saveText}
      </Button>
    ];

    const dateValue = (billingConfig && billingConfig.ledger_lock && billingConfig.ledger_lock >= model.billing_date) ? null : model.billing_date;

    const paymentMethods = currentCustomer && Object.values(currentCustomer.payment_config.payment_methods)
      .filter(method => method.enabled)
      .map(m => [m.display_name, m.canonical_name]) || [];

    const totalAmount = model.billing_item_amounts.some(x => x) && sum(model.billing_item_amounts.map(bia => parseCurrency(bia.amount) || 0)) || 0;
    const originalTotal = parseCurrency(model.amount) || 0;

    const handleAutoAndZeroDistributionChange = () => {
      // Removing all the billing item amounts
      model.billing_item_amounts.forEach(() => this.removeAmount(0));
      this.addAmount();
    };

    return (<ModalComponent buttons={buttons} title={titleize(model.type + ' details')} className="billing-item-editor">
        {this.state.preview
          ? <PreviewItems items={this.state.billingItems} />
          : <Form model={billingItemForm.model}>
          <ValidationErrors errors={model.errors} />

          <div>
            <ControlGroup label='Description'>
              <Control.text
                model='.description'
                className='form-control form-control-sm'
                placeholder='e.g. Tuition for Student'
              />
            </ControlGroup>

            {!edit && (
              <ControlGroup label='Distribution'>
                <div className='radio billing-item-editor-radio'>
                  <Control.text
                    type='radio'
                    value=''
                    checked={!model.distribution}
                    className='billing-item-editor-radio-btn'
                    model='.distribution'
                  />
                  <label className='billing-item-editor-radio-label'>Manual splits</label>
                </div>
                {(model.type === 'credit' || model.type === 'payment') && (
                  <div className='radio billing-item-editor-radio'>
                    <Control.text
                      type='radio'
                      value='auto'
                      checked={model.distribution === 'auto'}
                      className='billing-item-editor-radio-btn'
                      model='.distribution'
                      onChange={handleAutoAndZeroDistributionChange}
                    />
                    <label className='billing-item-editor-radio-label'>Auto distribute</label>
                  </div>
                )}
                {(model.type === 'credit' || model.type === 'charge') && (
                  <div className='radio billing-item-editor-radio'>
                    <Control.text
                      type='radio'
                      value='zero'
                      checked={model.distribution === 'zero'}
                      className='billing-item-editor-radio-btn'
                      model='.distribution'
                      onChange={handleAutoAndZeroDistributionChange}
                    />
                    <label className='billing-item-editor-radio-label'>Zero out contact</label>
                  </div>
                )}
              </ControlGroup>
            )}

            { model.type == 'payment' && (
              <React.Fragment>
                <ControlGroup label='Payment Method' showErrors>
                  <Control.select
                    className={'payment-method-select form-control form-control-sm'}
                    model={'.payment_method'}
                    disabled={linkedToEntity}
                  >
                    <option value={''}>Select payment method...</option>
                    {paymentMethods.map(([displayName, canonicalName]) => (
                      <option value={canonicalName}>{displayName}</option>
                    ))}
                  </Control.select>
                </ControlGroup>

                <ControlGroup label="Received Date" className="billing-item-received-date">
                  <DateSelector
                    model='.received_date'
                    modelValue={model.received_date}
                    className="form-control form-control-sm"
                    disabled={linkedToEntity}
                    isClearable={!linkedToEntity}
                  />
                  <div className="form-text">{global.I18n.date.formats.default_human}</div>
                </ControlGroup>
              </React.Fragment>
            )}

            {model.type !== 'payment' && (!linkedToEntity || amountsLocked) && <ControlGroup label={<>Recognized Date <OverlayTrigger
              placement='right'
              overlay={(
                <Tooltip id='tooltip-recognized-date'>
                  Date to use in Revenue Recognition Report and Incremental Billing Activity Report. Allows manually created
                  charges or credits split over several months to be treated as occurring in a single month, like contract installments.
                </Tooltip>
              )}
            >
              <FontAwesome name='info-circle' preNbsp/>
            </OverlayTrigger></>} className="billing-item-recognized-date">
              <DateSelector
                model='.recognized_date'
                modelValue={model.recognized_date}
                className="form-control form-control-sm"
                disabled={amountsLocked}
                isClearable={!amountsLocked}
              />
              <div className="form-text">{global.I18n.date.formats.default_human}</div>
            </ControlGroup>}

            {!amountsLocked && model.billing_item_amounts.map((amount, index) => {
              const requiresContact = autoDistribute || zeroOut
                || (amount.billing_category_id && billingCategories.ready
                  && categoryFromId(amount.billing_category_id, billingCategories).contact);

              const isCategoryWithoutContact = (amount.billing_category_id && billingCategories.ready
                && categoryFromId(amount.billing_category_id, billingCategories).contact === false);

              const choosableCategories = billingCategories.reduce(function(activeCats, cat) {
                if (cat.active) {
                  activeCats.push(cat);
                }
                return activeCats;
              }, []);

              let categoryGroupLabel = 'Category';
              if (autoDistribute) {
                categoryGroupLabel = 'Preferred Contact (optional)';
              } else if (zeroOut) {
                categoryGroupLabel = 'Contact (required)';
              }

              return ((!autoDistribute && !zeroOut) || index === 0) && (
                <div className='billing-item-amount' key={`billing-item-amount-${amount.id}`}>
                  {billingCategories.ready && index > 0 && (
                    <button
                      className='btn btn-sm btn-danger billing-item-editor-remove-button'
                      title='Remove Split'
                      onClick={() => this.removeAmount(index)}
                    >
                      <FontAwesome name='times' />
                    </button>
                  )}
                  <ControlGroup
                    label={categoryGroupLabel}
                    showErrors
                  >
                    { !autoDistribute && !zeroOut && (
                      <Placeholder.select ready={billingCategories.ready}>
                        <Control.select className="billing-category form-select form-select-sm form-control form-control-sm"
                          model={track(".billing_item_amounts[].billing_category_id", { id: amount.id })}
                        >
                          <option value="">Select category...</option>
                          {
                            choosableCategories.map(group => (
                              <optgroup key={group.id} label={group.name}>
                                <option value={group.id}>{group.name}</option>
                                {
                                  group.children.filter(c => c.active).map(category => (
                                    <option key={category.id} value={category.id}>{category.name}</option>
                                  ))
                                }
                              </optgroup>
                            ))
                          }
                        </Control.select>
                      </Placeholder.select>
                    ) }

                    {requiresContact && <Placeholder.select ready={billingContacts.ready} className="billing-contact">
                      <Control.select className="billing-contact form-select form-select-sm"
                                      model={track(".billing_item_amounts[].billing_contact_id", {id: amount.id})}>
                        <option value="">Select contact...</option>
                        {billingContacts.map(c => <option value={c.id} key={c.id}>{formatBillingContact(c)}</option>)}
                      </Control.select>
                    </Placeholder.select>}

                    {isCategoryWithoutContact && <Placeholder.select ready={this.validSchoolYears().length > 0} className="billing-contact">
                      <Control.select className="billing-contact form-select form-select-sm"
                                      model={track(".billing_item_amounts[].school_year_id", {id: amount.id})}>
                        <option value="">Select term...</option>
                        {this.validSchoolYears().map(schoolYear => <option value={schoolYear.id} key={schoolYear.id}>
                          {schoolYear.name}
                        </option>)}
                      </Control.select>
                    </Placeholder.select>}

                  </ControlGroup>

                  {!zeroOut && (
                    <ControlGroup label='Amount' showErrors>
                      <Control.text
                        type='number'
                        model={track('.billing_item_amounts[].amount', { id: amount.id })}
                        className='form-control form-control-sm'
                        placeholder='0.00'
                        controlProps={{ step: '0.01' }}
                      />
                    </ControlGroup>
                  )}
                </div>
              );
            })}

            {!amountsLocked && !autoDistribute && !zeroOut
              && (
                <ControlGroup>
                  <Button variant="success" size="sm" onClick={() => this.addAmount()}>Add Split</Button>
                </ControlGroup>
              )}

            {!amountsLocked && (model.billing_item_amounts.length > 1 || linkedToEntity) && (
              <React.Fragment>
                <ControlGroup label='Total Amount' className={classNames('total-amount', {
                  'error': linkedToEntity && (totalAmount != originalTotal)
                })}>
                  <input type={'text'}
                    value={formatCurrency(totalAmount)}
                    className={classNames('form-control form-control-sm')}
                    disabled
                  />
                </ControlGroup>

                {!!originalTotal &&
                  <ControlGroup label='Original Total Amount' className={classNames('original-total-amount', {
                    'error': linkedToEntity && (totalAmount != originalTotal)
                  })}>
                    <input type={'text'}
                      value={formatCurrency(originalTotal)}
                      className={classNames('form-control form-control-sm')}
                      disabled
                    />
                  </ControlGroup>
                }
              </React.Fragment>
            )}

            {/* when editing an item, show simple date control */}
            {edit && <ControlGroup label={<LedgerLockDateLabel billingConfig={billingConfig} />} className="billing-item-date">
              <DateSelector
                model='.billing_date'
                modelValue={dateValue}
                className='form-control form-control-sm'
                controlProps={ledgerLockCalendarParams(billingConfig)}
              />
              <div className="form-text">{global.I18n.date.formats.default_human}</div>
            </ControlGroup>}

            {/* when creating an item, show complex date control */}
            {!edit && <ControlGroup label="Posting Options">
              <div className={'radio billing-item-editor-radio'}>
                <Control.text type='radio'
                              value='once'
                              checked={model.post_type === 'once'}
                              className="billing-item-editor-radio-btn"
                              model='.post_type'/>
                <label className="billing-item-editor-radio-label">At once</label>
              </div>
              <ControlGroup label={<LedgerLockDateLabel billingConfig={billingConfig} />}
                            className={classNames('billing-item-date', {
                              'd-none': model.post_type !== 'once',
                              disabled: model.post_type !== 'once',
                              "billing-item-editor-subfield-date": model.post_type === 'once',
                            })}>
                <DateSelector
                  hidden={model.post_type !== 'once'}
                  controlProps={ledgerLockCalendarParams(billingConfig)}
                  model='.billing_date'
                  modelValue={dateValue}
                />
                <div className="form-text">{global.I18n.date.formats.default_human}</div>
              </ControlGroup>

              <div className={'radio billing-item-editor-radio'}>
                <Control.text type='radio'
                              value="split"
                              checked={model.post_type === 'split'}
                              className="input-small billing-item-editor-radio-btn"
                              model='.post_type'/>
                  <label className="billing-item-editor-radio-label">Evenly across months</label>
                <div className={"form-text italic"}>Spread the amount evenly across multiple months. Ledger items will be posted according to account’s billing day.</div>
              </div>

              <ControlGroup label="Start Year" className={model.post_type !== 'split' ? 'd-none disabled' : 'billing-item-editor-subfield'}>
                <Control.select defaultValue={model.start_year || currentYear} model='.start_year'>
                  {years.map((year) => <option value={year}>{year}</option>).map(ensureKeyed())}
                </Control.select>
              </ControlGroup>

              <ControlGroup label="Start Month" className={model.post_type !== 'split' ? 'd-none disabled' : 'billing-item-editor-subfield'}>
                <Control.select defaultValue={model.start_month || currentMonth} model='.start_month'>
                  {Object.keys(MONTHS).map((month) => <option
                    value={MONTHS[month]}>{month}</option>).map(ensureKeyed())}
                </Control.select>
              </ControlGroup>

              <ControlGroup label="Number of Months" className={model.post_type !== 'split' ? 'd-none disabled' : 'billing-item-editor-subfield'}>
                <Control.select defaultValue={model.number_of_months || 2} model='.number_of_months' hidden={model.post_type !== 'split'}>
                  {Object.values(MONTHS).map((number) => <option
                    value={number + 1}>{number + 1}</option>).map(ensureKeyed())}
                </Control.select>
              </ControlGroup>
            </ControlGroup>}
          </div>
        </Form>}
      </ModalComponent>
    );
  }
}

const PreviewItems = ({ items }) => (
  <table className='billing-item-list table table-borderless'>
    <thead>
      <tr>
        <th />
        <th>Billing Date</th>
        <th className='billing-item-preview-contact-column'>Contact</th>
        <th>Category</th>
        <th>Billing Amount</th>
      </tr>
    </thead>
    {items.map((item) => (
      <PreviewItem item={item} />
    )).map(ensureKeyed())}
    <TotalRow items={items} />
  </table>
);
PreviewItems.propTypes = {
  items: PropTypes.arrayOf(BillingItemType).isRequired
};

const PreviewItem = ({ item }) => {
  const [collapsed, setCollapsed] = useState(true);
  return (
    <tbody>
      <tr key={item.id}>
        <td>
          <button
            type='button'
            className='billing-item-preview-expand-button'
            onClick={() => setCollapsed(!collapsed)}
          >
            <FontAwesome name={collapsed ? 'caret-right' : 'caret-down'} />
          </button>
        </td>
        <td>{formatDate(item.billing_date)}</td>
        <td />
        <td />
        <td>{formatCurrency(item.amount)}</td>
      </tr>
      {!collapsed && item.billing_item_amounts.map((amount) => (
        <tr key={amount.id}>
          <td />
          <td />
          <td className='sub'>
            {(amount.billing_contact !== null ? formatBillingContact(amount.billing_contact) : '')}
          </td>
          <td className='sub'>{amount.billing_category.name}</td>
          <td className='sub'>{formatCurrency(amount.amount)}</td>
        </tr>
      ))}
    </tbody>
  );
};
PreviewItem.propTypes = {
  item: BillingItemType.isRequired
};

const TotalRow = ({ items }) => (
  <tbody>
    <tr>
      <td />
      <td><b>Total Amount</b></td>
      <td />
      <td />
      <td>
        {formatCurrency(items.reduce(
          (previous, current) => previous.add(new BigNumber(current.amount)),
          new BigNumber(0)
        ))}
      </td>
    </tr>
  </tbody>
);
TotalRow.propTypes = {
  items: PropTypes.arrayOf(BillingItemType).isRequired
};

const mapDucksToProps = () => ({
  currentCustomer,
  billingConfig: singleDuck(billingConfigsDuck, 'current')
});

export default duckify(mapDucksToProps, BillingItemEditor.mapStateToProps)(BillingItemEditor);
